import { HEADER_BYTES, MALFORMED, OK, viewOf, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { decodeSnapshotFrame } from "./snapshot-decode.ts";
import { Component, encodeSnapshotFrame, newSnapshotRecord, type DecodedEntity, type EntityState, type SnapshotRecord } from "./snapshot.ts";

/**
 * The `edgeView` frame (`0x070E`, PROTOCOL.md 5.24, ADR-147 decisions 12 and 31): what stands on one map's ground within
 * the band along a seam, for the neighbouring map's players to draw. It is the snapshot's header and keyframe records
 * under a type of its own, so a gateway fans it out by type without decoding it, with the sending map's manifest index
 * where the snapshot holds `ackSeq`. Every entity carries its Name, since the frame has no earlier view to take one
 * from. The encoder allocates nothing; the decoder never throws.
 */

/**
 * The most entities one edge view holds (ADR-147 decision 12, `EDGE_VIEW_MAX_ENTITIES`).
 */
export const EDGE_VIEW_MAX_ENTITIES = 48;

/**
 * The largest manifest map index the header carries: a `u16`.
 */
export const MAX_EDGE_VIEW_MAP = 0xffff;

const MAP_OFFSET = HEADER_BYTES + 8;
const NO_REMOVED: readonly number[] = Object.freeze([]);
const NAME_ONLY = (): number => Component.Name;

/**
 * What the encoder needs for one edge view. `entities` are the sending map's own, nearest the line first, with positions
 * in its own frame and its own handles.
 */
export interface EdgeViewInput {
  readonly tick: number;
  readonly map: number;
  readonly entities: readonly EntityState[];
}

/**
 * A decoded edge view, reused across frames on the client; entity records are rebuilt per frame.
 */
export interface EdgeViewRecord {
  tick: number;
  map: number;
  entities: DecodedEntity[];
  readonly scratch: SnapshotRecord;
}

/**
 * A fresh record for `decodeEdgeView` to fill.
 *
 * @returns An empty record.
 */
export function newEdgeViewRecord(): EdgeViewRecord {
  return { tick: 0, map: 0, entities: [], scratch: newSnapshotRecord() };
}

/**
 * Encodes an edge view into a caller-owned buffer.
 *
 * @param buffer Destination.
 * @param input The tick, the sending map's manifest index and its entities.
 * @returns Bytes written.
 * @throws Error when a value does not fit its field, the buffer is too small, the map is past `MAX_EDGE_VIEW_MAP` or
 *   there are more than `EDGE_VIEW_MAX_ENTITIES` entities.
 */
export function encodeEdgeView(buffer: Uint8Array, input: EdgeViewInput): number {
  if (input.entities.length > EDGE_VIEW_MAX_ENTITIES) {
    throw new Error(`edge view of ${input.entities.length} entities is over the ${EDGE_VIEW_MAX_ENTITIES} a frame holds`);
  }
  if (!Number.isInteger(input.map) || input.map < 0 || input.map > MAX_EDGE_VIEW_MAP) {
    throw new Error(`edge view map ${input.map} does not fit in 0 to ${MAX_EDGE_VIEW_MAP}`);
  }
  return encodeSnapshotFrame(buffer, { tick: input.tick, baseTick: input.tick, ackSeq: input.map, keyframe: true, entities: input.entities, dirty: NAME_ONLY, removed: NO_REMOVED }, MessageType.EdgeView);
}

/**
 * Decodes an edge view. Never throws: a snapshot's faults close with the malformed code, and so does an edge view with
 * more than `EDGE_VIEW_MAX_ENTITIES` entities, a map past `MAX_EDGE_VIEW_MAP`, a frame that is not a keyframe, a base
 * unlike the tick, a removed or crossed list, or a record without its Name. The record's own scratch is reused.
 *
 * @param frame The whole frame.
 * @param out Record to fill; its contents are unspecified unless the verdict is `ok`.
 * @returns The verdict.
 */
export function decodeEdgeView(frame: Uint8Array, out: EdgeViewRecord): DecodeVerdict {
  const scratch = out.scratch;
  const verdict = decodeSnapshotFrame(frame, scratch, MessageType.EdgeView);
  if (verdict.kind !== "ok") {
    return verdict;
  }
  const view = viewOf(frame);
  const map = view.getUint32(MAP_OFFSET, true);
  if (map > MAX_EDGE_VIEW_MAP || scratch.baseTick !== scratch.tick || scratch.removed.length > 0 || scratch.crossed.length > 0 || scratch.entities.length > EDGE_VIEW_MAX_ENTITIES || !scratch.keyframe) {
    return MALFORMED;
  }
  for (const entity of scratch.entities) {
    if ((entity.flags & Component.Name) === 0 || (entity.flags & Component.Spawn) === 0) {
      return MALFORMED;
    }
  }
  out.tick = scratch.tick;
  out.map = map;
  out.entities = scratch.entities;
  return OK;
}
